import { transport } from '@opengov/app-core/transport';
import { getNodeById, hasActiveDescendent, nodeIsParent } from '@opengov/component-library/capital';
import { FACEBOOK, GRAPH, SORT_NAME_A_Z, SORT_NAME_Z_A, TABLE, TWITTER, X, STATE_DRAFT, STATE_INTERNAL_PUBLISHED, STATE_PUBLISHED } from '@opengov/stories-shared/constants';
import { IMediaLink, IStoryContent } from '@opengov/stories-shared/types';
import { cloneDeep } from 'lodash';

import {
  ICheckboxStoryNode,
  IGraphQLQuery,
  IStoriesFolder,
  IStoryFolder,
  IStoryPrivilege,
  ISubjectClaims,
  StoryEmbedListData,
  IExtendedStoryPrivilege,
  IPublicationFolder,
  IPublicationStoryFolder
} from '../../types';

import { EMBED_REQUEST_TYPE } from '../constants';

export enum EDepthType {
  FROM_TOP = 'fromTop',
  MAX_CHILDREN_DEPTH = 'maxChildrenDepth'
}

export const getNewestUpdatedAt = (storyContents: IStoryContent[], updatedAt: string): string[] =>
  storyContents
    .reduce((updated: string[], storyContent: IStoryContent) => {
      if (storyContent.updatedAt) updated.push(storyContent.updatedAt as string);
      return updated;
    }, [])
    .concat(updatedAt)
    .sort((a: string, b: string) => new Date(b).getTime() - new Date(a).getTime());

export const gql = (...strings): string => {
  return strings.join('');
};

export const orderBy = (sort: string): { [key: string]: string } | { [key: string]: string }[] => {
  let order;
  switch (sort) {
    case 'title':
      order = { title: 'asc' };
      break;
    case '-title':
      order = { title: 'desc' };
      break;
    case 'createdAt':
      order = { createdAt: 'asc' };
      break;
    case '-createdAt':
      order = { createdAt: 'desc' };
      break;
    case 'updatedAt':
      order = { updatedAt: 'asc' };
      break;
    case '-updatedAt':
      order = { updatedAt: 'desc' };
      break;
    case 'custom':
      order = [{ order: 'asc' }, { title: 'asc' }];
    default:
  }
  return order;
};

export const postQuery = async (query: IGraphQLQuery): Promise<any> =>
  transport
    .url(`/api/data/objects/stories/v1/graphql`)
    .json(query)
    .post()
    .json()
    .then((response) => (response.errors ? Promise.reject(response.errors) : response));

export const transformFolderStructureToMap = (folderStructure: IStoriesFolder[]): { [key: string]: IStoriesFolder } => {
  const folderMap: { [key: string]: IStoriesFolder } = {};

  const addToFolderMap = (folderNodes: IStoriesFolder[], depth = 1, parent = null) => {
    if (folderNodes) {
      folderNodes.forEach((folder) => {
        folderMap[folder.id] = {
          ...folder,
          parent,
          depth
        };
        if (folder.children) {
          addToFolderMap(folder.children, depth + 1, folder.id);
        }
      });
    }
  };

  addToFolderMap(folderStructure);

  return folderMap;
};

export const getImmediateChildrenDisplayNames = (publicationFolders: IStoriesFolder[], parentId: string): string[] => {
  const findNodeById = (nodes: IStoriesFolder[], id: string): IStoriesFolder | null => {
    for (const node of nodes) {
      if (node.id === id) {
        return node;
      } else if (node.children && Array.isArray(node.children)) {
        const found = findNodeById(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const parentNode = findNodeById(publicationFolders, parentId);

  if (parentNode && Array.isArray(parentNode.children)) {
    return parentNode.children.map((child) => child.displayName);
  }

  return [];
};

// return an updated folder tree with fields in a specific node updated
// all updates to a node are partial updates unless a complete new node is passed
export const updateFolderStructureNode = (
  folderStructure: IStoriesFolder[],
  updatedFolderNode: Partial<IStoriesFolder>
): IStoriesFolder[] => {
  const updateNodes = (folderNodes: IStoriesFolder[]): IStoriesFolder[] => {
    if (folderNodes) {
      return folderNodes.map((folderNode) => {
        if (folderNode.id === updatedFolderNode.id) {
          return { ...folderNode, ...updatedFolderNode };
        } else {
          let children = folderNode.children;
          if (folderNode.children && folderNode.children.length > 0) {
            children = updateNodes(folderNode.children);
          }
          return { ...folderNode, children };
        }
      });
    }
  };
  const newFolderStructure = [...folderStructure];
  if (updatedFolderNode && updatedFolderNode.id) {
    return updateNodes(newFolderStructure);
  }
  return folderStructure;
};


export const getPublicationDepthInfo = (tree: IStoriesFolder[], targetId: string, mode = EDepthType.FROM_TOP): number | null => {
  function findNodeWithPath(nodes: IStoriesFolder[], targetId: string, depth = 0) {
    for (const node of nodes) {
      if (node.id === targetId) {
        return { node, depth };
      }
      if (Array.isArray(node.children)) {
        const result = findNodeWithPath(node.children, targetId, depth + 1);
        if (result) return result;
      }
    }
    return null;
  }

  function getMaxDepth(node: IStoriesFolder, currentDepth = 0): number {
    if (!node.children || !node.children.length) return currentDepth;

    let max = currentDepth;
    for (const child of node.children) {
      const childDepth = getMaxDepth(child, currentDepth + 1);
      if (childDepth > max) max = childDepth;
    }
    return max;
  }

  const result = findNodeWithPath(tree, targetId);

  if (!result) return null;

  if (mode === EDepthType.FROM_TOP) {
    return result.depth;
  }

  if (mode === EDepthType.MAX_CHILDREN_DEPTH) {
    return getMaxDepth(result.node as IStoriesFolder, 0);
  }

  throw new Error(`Invalid mode: ${mode}. Use "fromTop" or "maxChildrenDepth"`);
}

// returns array of node ids of all chidren of supplied folder node
export const getNodeDescedentIds = (folderNode: IStoriesFolder): string[] => {
  const descendentIds: string[] = [];

  const addDescendentIds = (node: IStoriesFolder) => {
    if (node && node.children) {
      node.children.forEach((childFolderNode) => {
        descendentIds.push(childFolderNode.id);
        addDescendentIds(childFolderNode);
      });
    }
  };

  addDescendentIds(folderNode);

  return descendentIds;
};

// returns array of node ids of all chidren of supplied folder node
export const isTopLevelNode = (folderStructure: IStoriesFolder[], id: string): boolean => {
  if (folderStructure) {
    return !!folderStructure.find((node) => node.id === id);
  }
  return false;
};

export const getParentNodeForNode = (
  childNode: IStoriesFolder,
  folderStructure: IStoriesFolder[],
  publicationRootFolderId?: string
): IStoriesFolder | string => {
  if (!childNode || !folderStructure) return null;
  let noParent = false;
  let parent = null;
  const searchForParent = (nodeList: IStoriesFolder[]) => {
    nodeList.forEach((n) => {
      if (nodeIsParent(n, childNode.id)) {
        parent = n;
      } else if (n.children) {
        searchForParent(n.children);
      }
    });
  };
  folderStructure.forEach((node: IStoriesFolder) => {
    if (childNode && childNode.id === node.id) {
      noParent = true;
    }
  });
  searchForParent(folderStructure);
  if (noParent) {
    return publicationRootFolderId ? publicationRootFolderId : null;
  } else {
    return parent;
  }
};

// returns the number of nodes above the specified node (not inclusive)
export const countAncestors = (nodeTree: IStoriesFolder[], nodeId: string): number => {
  if (!nodeId || !nodeTree) return;
  let tally = 0;
  const findAndTally = (nodeList: IStoriesFolder[]) => {
    if (!nodeList) return;
    if (nodeList.find((n) => n.id === nodeId)) {
      return;
    } else {
      const ancestor = nodeList.find((n) => hasActiveDescendent(n, nodeId));
      tally++;
      if (ancestor) {
        findAndTally(ancestor.children);
      }
    }
  };
  findAndTally(nodeTree);
  return tally;
};

// returns the maximum depth of nodes below the specified node (not inclusive)
export const getDescendentTreeHeight = (nodeTree: IStoriesFolder[], nodeId: string): number => {
  const node = getNodeById(nodeId, nodeTree);
  if (!node || !nodeTree) {
    return;
  }
  if (node && !node.children) {
    return 0;
  }
  let tally = 1;
  const searchAndTally = (nodeList: IStoriesFolder[]) => {
    if (!nodeList) return;
    const generation: IStoriesFolder[] = [];
    nodeList.forEach((n) => {
      if (n.children) {
        n.children.forEach((childNode) => generation.push(childNode));
      }
    });
    if (generation.length === 0) return;
    tally++;
    searchAndTally(generation);
  };
  searchAndTally(node.children);
  return tally;
};

export const getPathForFolder = (folderStructure: IStoriesFolder[], id: string): IStoriesFolder[] => {
  const path: IStoriesFolder[] = [];

  const addPath = (folders: IStoriesFolder[]) => {
    const ancestor = folders && folders.find((n) => n.id === id || hasActiveDescendent(n, id));
    if (ancestor) {
      if (ancestor.id === id) {
        path.push(ancestor);
      } else {
        path.push(ancestor);
        addPath(ancestor.children);
      }
    }
  };

  addPath(folderStructure);

  return path;
};

// sets node and its descedents to all be disabled, optionally applies a tooltip message
export const disableNodeAndDescendants = (node: IStoriesFolder, disabledNodeTooltip?: string): IStoriesFolder => {
  if (node) {
    const updatedNode = { ...node };
    updatedNode.disabled = true;
    updatedNode.tooltip = disabledNodeTooltip;
    if (updatedNode.children) {
      updatedNode.children = updatedNode.children.map((nodeChild) =>
        disableNodeAndDescendants(nodeChild, disabledNodeTooltip)
      );
    }
    return updatedNode;
  }
  return node;
};

export const disableInvalidNodes = (
  folderTree: IStoriesFolder[],
  isNodeValid: (folder: IStoriesFolder) => boolean,
  disabledNodeTooltip?: string
): IStoriesFolder[] => {
  return cloneDeep(folderTree).map((folder) => {
    if (isNodeValid(folder)) {
      return disableNodeAndDescendants(folder, disabledNodeTooltip);
    }
    if (folder.children) {
      folder.children = disableInvalidNodes(folder.children, isNodeValid, disabledNodeTooltip);
    }
    return folder;
  });
};

// determines whether specified folder ids would be nested too deep if nested at the target folder depth
export const areFoldersNestedTooDeep = (
  folderStructure: IStoriesFolder[],
  folderIds: string[],
  targetFolderDepth: number
): boolean => {
  let isTooDeep = false;
  if (folderIds && folderStructure) {
    folderIds.forEach((folderId) => {
      const treeHeight = getDescendentTreeHeight(folderStructure, folderId);
      if (treeHeight + targetFolderDepth > 5) {
        isTooDeep = true;
      }
    });
  }
  return isTooDeep;
};

export const compileNodes = (nodeList) => {
  if (!nodeList) return;
  return (
    nodeList &&
    nodeList.map((node) => {
      const newNode = {
        id: null,
        displayName: null,
        children: null,
        toggled: false,
        order: 0
      };
      newNode.id = node.id;
      newNode.displayName = node.title;
      newNode.order = node.order;
      if (node.subFolders && node.subFolders.length > 0) {
        newNode.children = compileNodes(node.subFolders);
      }
      return newNode;
    })
  );
};

export const compilePublicationNodes = (nodeList) => {
  if (!nodeList) return;
  return (
    nodeList &&
    nodeList.map((nodeParent) => {
      const node = ((nodeParent.storyFolder) ? nodeParent.storyFolder : nodeParent) as IPublicationStoryFolder;
      const newNode: IPublicationStoryFolder = {
        id: '',
        title: '',
        displayName: '',
        children: [],
        toggled: false,
        order: 0
      };
      newNode.id = (node.title.includes('root-folder')) ? node.publication?.id : node.id;
      newNode.displayName = (node.title.includes('root-folder')) ? (node.publication?.title || node.title) : node.title;
      newNode.order = node.order;
      if (node.subFolders && node.subFolders.length > 0) {
        newNode.children = compilePublicationNodes(node.subFolders);
      }
      return newNode;
    })
  );
};

// sorts a list of nodes alphabetically
export const alphabetizeNodes = (nodeList: IStoriesFolder[]): IStoriesFolder[] => {
  return nodeList
    .sort((first, second) => {
      const firstDisplayName = first.displayName?.toLowerCase();
      const secondDisplayName = second.displayName?.toLowerCase();
      return firstDisplayName < secondDisplayName ? -1 : 1;
    })
    .map((node) => {
      if (node.children) {
        const alphabetizedChildFolders = alphabetizeNodes(node.children);
        const childFoldersSortedByOrder = alphabetizedChildFolders.sort((first, second) => {
          return first.order < second.order ? -1 : 1;
        });
        node.children = childFoldersSortedByOrder;
      }
      return node;
    });
};

// sorts a list of nodes with order if order is set otherwise sort alphabetically
export const orderNodes = (nodeList: IStoriesFolder[]): IStoriesFolder[] => {
  return nodeList
    .map((node) => {
      if (node.children) {
        const alphabetizedChildFolders = alphabetizeNodes(node.children);
        const childFoldersSortedByOrder = alphabetizedChildFolders.sort((first, second) => {
          if (first.order)
            return first.order < second.order ? -1 : 1;
          return first.displayName.toLowerCase() < second.displayName.toLowerCase() ? -1 : 1;
        });
        node.children = childFoldersSortedByOrder;
      }
      return node;
    });
};

export const getFolderById = (
  nodeList: (IStoryFolder | IStoriesFolder)[],
  nodeId: string
): IStoryFolder | IStoriesFolder => {
  let node = null;
  const findAndAssignNode = (nodes: (IStoriesFolder | IStoryFolder)[]) =>
    nodes.forEach((n: IStoriesFolder | IStoryFolder) => {
      if (node !== null) {
        return;
      }
      if (n.id === nodeId) {
        node = n;
      } else if ((n as IStoryFolder).subFolders || (n as IStoriesFolder).children) {
        findAndAssignNode((n as IStoryFolder).subFolders || (n as IStoriesFolder).children);
      }
    });
  findAndAssignNode(nodeList);
  return node;
};

export const groupAllStoryFolders = (nodeList: { storyFolder: IStoryFolder }[]): IStoryFolder[] => {
  const node: IStoryFolder[] = [];
  nodeList.forEach(({ storyFolder }) => {
    if (storyFolder) {
      node.push(storyFolder);
    }
  });
  return node;
};

export const sortItemsByOrder = (items) => {
  return items.sort((first, second) => {
    return first.order < second.order ? -1 : 1;
  });
};

export const getMediaLinks = (mediaLinks: IMediaLink[]): IMediaLink[] => {
  return mediaLinks.filter(
    (mediaLink) => mediaLink.type !== FACEBOOK && mediaLink.type !== TWITTER && mediaLink.type !== X
  );
};

export const getFacebookLink = (mediaLinks: IMediaLink[]): IMediaLink => {
  return mediaLinks.find((mediaLink) => mediaLink.type === FACEBOOK);
};

// Matches both the new `X` type and the legacy `TWITTER` type so publications
// created before the Twitter -> X rebrand continue to work.
export const getXLink = (mediaLinks: IMediaLink[]): IMediaLink => {
  return mediaLinks.find((mediaLink) => mediaLink.type === X || mediaLink.type === TWITTER);
};

export const getNodesWithStories = (nodes: ICheckboxStoryNode[]): ICheckboxStoryNode[] => {
  const rootNodes = [];
  nodes.forEach((nodeElement) => {
    const filterNode = (el: ICheckboxStoryNode) => {
      const element = { ...el };
      element.subFolders = [];
      if (el?.subFolders?.length) {
        element.subFolders = getNodesWithStories(el.subFolders);
      }
      if (element.stories.length || element.subFolders.length) {
        rootNodes.push(element);
      }
    };
    filterNode(nodeElement);
  });
  return rootNodes;
};

export const purgeUrlCache = (url: string): string => {
  const timestamp = new Date().toJSON().toString();
  return `${url}?v=${timestamp}`;
};

export const getReportType = (type: string): string => {
  return type === EMBED_REQUEST_TYPE.DetailTable || type === EMBED_REQUEST_TYPE.SummaryTable ? TABLE : GRAPH;
};

export const sortAlphabetically = (data: StoryEmbedListData[], sort: string): StoryEmbedListData[] => {
  let sortedValues = data;
  if (sort === SORT_NAME_A_Z) {
    sortedValues = data.sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));
  }
  if (sort === SORT_NAME_Z_A) {
    sortedValues = data.sort((b, a) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));
  }
  return sortedValues;
};

export function findPublicationIdBySectionId(data: IStoriesFolder[], sectionId: string) {
  const parentMap = new Map();

  function buildParentMap(items: IStoriesFolder[], parentID = null) {
    for (const item of items) {
      parentMap.set(item.id, parentID);
      if (item.children) {
        buildParentMap(item.children, item.id);
      }
    }
  }

  buildParentMap(data);

  let currentID = sectionId;
  while (parentMap.has(currentID) && parentMap.get(currentID) !== null) {
    currentID = parentMap.get(currentID);
  }

  return currentID; // The highest-level parent ID
}

export const hasPrivilege = (currentSubjectClaims: ISubjectClaims | null, privilegeName: string): boolean => {
  if (!currentSubjectClaims || !currentSubjectClaims.claims) return false;

  return currentSubjectClaims.claims.some((claim: { claimName: string; granted: boolean }) =>
    (claim.claimName === privilegeName && claim.granted)
  );
};

export const hasSubjectStoryClaim = (
  currentSubjectClaims: ISubjectClaims | null,
  currentSubjectPublicationStoryClaim: IExtendedStoryPrivilege | null | undefined,
  privilegeName: IStoryPrivilege
): boolean => {
  // Check for STORIES_ADMIN_PRIVILEGE in currentSubjectClaims
  const isAdmin = currentSubjectClaims?.claims?.some(
    (claim) => claim.claimName === 'STORIES_ADMIN_PRIVILEGE' && claim.granted
  );

  if (isAdmin) return true;

  // Honor entity-level (role-based) grants from the authoritative claims/verify
  // response. A user with an entity-wide privilege (e.g. a reviewer role that
  // grants STORIES_VIEWER_PRIVILEGE) may access publication stories even when
  // they have no granular per-story claim row (allClaims === []).
  const hasEntityPrivilege = currentSubjectClaims?.claims?.some(
    (claim) => claim.claimName === privilegeName && claim.granted
  );

  if (hasEntityPrivilege) return true;

  // Fallback to checking specific story-level claims
  if (!currentSubjectPublicationStoryClaim) return false;

  return Array.isArray(currentSubjectPublicationStoryClaim) && currentSubjectPublicationStoryClaim.includes(privilegeName);
};

/**
 * Converts story status constant to display name
 * @param status - The story status constant
 * @returns The display name for the status
 */
export const getStoryStatusDisplayName = (status: string): string => {
  switch (status) {
    case STATE_DRAFT:
      return 'Draft';
    case STATE_INTERNAL_PUBLISHED:
      return 'Internal';
    case STATE_PUBLISHED:
      return 'Public';
    default:
      return '';
  }
};

/**
 * Recursively maps publication subfolders to the required folder structure format
 * @param subFolders - Array of subfolders to map
 * @returns Array of mapped publication folders with nested subfolders
 */
export const mapPublicationSubFoldersRecursively = (
  subFolders: IStoryFolder[]
): IPublicationFolder[] => {
  if (!subFolders || subFolders.length === 0) return [];

  return subFolders.map((subFolder) => ({
    storyFolder: {
      id: subFolder.id,
      title: subFolder.title,
      displayName: subFolder.title,
      publication: subFolder.publication,
      subFolders: mapPublicationSubFoldersRecursively(subFolder.subFolders)
    }
  }));
};
